/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useReducer } from "react";
import { toast } from "../../services/toast";

import { useNavigate } from "react-router-dom";

import { makeStyles } from 'tss-react/mui';
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Chip from "@mui/material/Chip";
import ClearIcon from "@mui/icons-material/Clear";
import CloudIcon from "@mui/icons-material/Cloud";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import TimerOffIcon from "@mui/icons-material/TimerOff";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid } from "@mui/material";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import { socketConnection } from "../../services/socket";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((u) => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }

    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;

    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles()((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    width: "100%",
    flexWrap: "wrap",
  },
  acciones: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  filtro: {
    "& .MuiToggleButton-root": {
      textTransform: "none",
      fontWeight: 600,
      paddingTop: 6,
      paddingBottom: 6,
      lineHeight: 1.4,
      color: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
    },
    "& .Mui-selected": {
      color: theme.palette.primary.main,
    },
  },
  buscador: {
    minWidth: 240,
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.borderRadius,
      backgroundColor:
        theme.palette.mode === "light"
          ? theme.palette.background.paper
          : "rgba(255,255,255,0.04)",
    },
  },
  btnNueva: {
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  chipMeta: {
    height: 20,
    fontSize: 11,
    fontWeight: 700,
  },

  "@keyframes latido": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.45 },
  },
  chipLatiendo: {
    animation: "$latido 1.4s ease-in-out infinite",
  },
  cuentaAtras: {
    marginTop: 3,
    fontSize: 11,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.text.secondary,
  },
}));

const ESTADO_TONO = {
  INACTIVA: "#78909c",
  PROGRAMADA: "#0288d1",
  PREPARANDO: "#7b1fa2",
  EM_ANDAMENTO: "#ed6c02",
  FINALIZADA: "#2e7d32",
  CANCELADA: "#c62828",
};

const Campaigns = () => {
  const { classes } = useStyles();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [canal, setCanal] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);

  const [tick, setTick] = useState(Date.now());

  const { datetimeToClient } = useDate();
  
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, canal]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, canal, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-campaign`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    const hayCuentaAtras = campaigns.some(
      (c) =>
        c.status === "PROGRAMADA" ||
        (c.status === "EM_ANDAMENTO" && !Number(c.entregados))
    );
    if (!hayCuentaAtras) return undefined;

    const reloj = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(reloj);
  }, [campaigns]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", {
        params: { searchParam, pageNumber, canal: canal || undefined },
      });
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const cuentaAtras = (scheduledAt) => {
    if (!scheduledAt) return null;

    const faltan = new Date(scheduledAt).getTime() - tick;
    if (Number.isNaN(faltan)) return null;

    if (faltan <= 0) return "debió salir ya";

    const s = Math.floor(faltan / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const seg = s % 60;

    if (d > 0) return `en ${d} d ${h} h`;
    if (h > 0) return `en ${h} h ${m} min`;
    if (m > 0) return `en ${m} min ${String(seg).padStart(2, "0")} s`;
    return `en ${seg} s`;
  };

  const estadoVisual = (campaign) => {
    if (campaign.status !== "EM_ANDAMENTO") return campaign.status;

    return Number(campaign.entregados) > 0 ? "EM_ANDAMENTO" : "PREPARANDO";
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INACTIVA":
        return "Inactiva";
      case "PROGRAMADA":
        return "Programada";
      case "PREPARANDO":
        return "Preparando";
      case "EM_ANDAMENTO":
        return "Enviando";
      case "CANCELADA":
        return "Cancelada";
      case "FINALIZADA":
        return "Finalizada";
      default:
        return val;
    }
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <MainContainer maxWidth={false}>
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${deletingCampaign.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <CampaignModal
        resetPagination={() => {
          setPageNumber(1);
          fetchCampaigns();
        }}
        open={campaignModalOpen}
        onClose={handleCloseCampaignModal}
        aria-labelledby="form-dialog-title"
        campaignId={selectedCampaign && selectedCampaign.id}
      />
      <MainHeader>
        <div className={classes.header}>
          <Title>{i18n.t("campaigns.title")}</Title>

          <div className={classes.acciones}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={canal}
              onChange={(e, v) => setCanal(v === null ? "" : v)}
              className={classes.filtro}
            >
              <ToggleButton value="">Todos</ToggleButton>
              <ToggleButton value="whatsapp_cloud">
                <CloudIcon fontSize="small" sx={{ mr: 0.5 }} />
                WhatsApp Cloud
              </ToggleButton>
              <ToggleButton value="whatsapp">
                <WhatsAppIcon fontSize="small" sx={{ mr: 0.5 }} />
                WhatsApp
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              className={classes.buscador}
              size="small"
              variant="outlined"
              placeholder={i18n.t("campaigns.searchPlaceholder")}
              value={searchParam}
              onChange={handleSearch}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="disabled" />
                    </InputAdornment>
                  ),
                  endAdornment: searchParam ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchParam("")}
                        edge="end"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }
              }}
            />

            <Button
              variant="contained"
              onClick={handleOpenCampaignModal}
              color="primary"
              disableElevation
              className={classes.btnNueva}
            >
              {i18n.t("campaigns.buttons.add")}
            </Button>
          </div>
        </div>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                {i18n.t("campaigns.table.name")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("campaigns.table.status")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("campaigns.table.contactList")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("campaigns.table.whatsapp")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("campaigns.table.scheduledAt")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("campaigns.table.completedAt")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("campaigns.table.confirmation")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("campaigns.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {campaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell align="center">
                    {campaign.name}
                    {campaign.templateId && (
                      <Tooltip
                        title={
                          campaign.template?.name
                            ? `Plantilla: ${campaign.template.name}`
                            : "Sale por plantilla de Meta"
                        }
                      >
                        <Chip
                          icon={<CloudIcon />}
                          label="Cloud"
                          size="small"
                          className={classes.chipMeta}
                          sx={{
                            ml: 0.75,
                            color: "#fff",
                            backgroundColor: "#0084FF",
                            "& .MuiChip-icon": { color: "#fff", fontSize: 13 },
                          }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {(() => {
                      const visual = estadoVisual(campaign);
                      const contando =
                        visual === "PROGRAMADA" || visual === "PREPARANDO";

                      return (
                        <>
                          <Chip
                            size="small"
                            label={formatStatus(visual)}
                            className={
                              visual === "EM_ANDAMENTO"
                                ? classes.chipLatiendo
                                : undefined
                            }
                            sx={{
                              fontWeight: 700,
                              color: "#fff",
                              backgroundColor:
                                ESTADO_TONO[visual] || ESTADO_TONO.INACTIVA,
                            }}
                          />

                          {contando && campaign.scheduledAt && (
                            <div className={classes.cuentaAtras}>
                              {cuentaAtras(campaign.scheduledAt)}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    {campaign.contactListId
                      ? campaign.contactList.name
                      : "No definido"}
                  </TableCell>
                  <TableCell align="center">
                    {campaign.whatsappId
                      ? campaign.whatsapp.name
                      : "No definido"}
                  </TableCell>
                  <TableCell align="center">
                    {campaign.scheduledAt
                      ? datetimeToClient(campaign.scheduledAt)
                      : "Sin horario"}
                  </TableCell>
                  <TableCell align="center">
                    {campaign.completedAt
                      ? datetimeToClient(campaign.completedAt)
                      : "Sin completar"}
                  </TableCell>
                  <TableCell align="center">
                    {campaign.confirmation ? "Habilitada" : "Desabilitada"}
                  </TableCell>
                  <TableCell align="center">
                    {campaign.status === "EM_ANDAMENTO" && (
                      <IconButton
                        onClick={() => cancelCampaign(campaign)}
                        title="Detener campaña"
                        size="small"
                      >
                        <PauseCircleOutlineIcon />
                      </IconButton>
                    )}
                    {campaign.status === "CANCELADA" && (
                      <IconButton
                        onClick={() => restartCampaign(campaign)}
                        title="Detener campaña"
                        size="small"
                      >
                        <PlayCircleOutlineIcon />
                      </IconButton>
                    )}
                    <IconButton
                      onClick={() =>
                        navigate(`/campaign/${campaign.id}/report`)
                      }
                      size="small"
                    >
                      <DescriptionIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditCampaign(campaign)}
                    >
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setConfirmModalOpen(true);
                        setDeletingCampaign(campaign);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={8} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Campaigns;
